<template>
  <div class="contents">
    <div class="layout">

      <div class="page-top">
        <h2>배너 관리</h2>
        <div class="breadcrumbs">
          <span>컨텐츠 관리</span>
          <span class="color-black">배너 관리</span>
        </div>
      </div>

      <div class="white-panel mt30 pt30 pb30">
        <div class="search-panel">
          <table>
            <colgroup>
              <col style="width:150px;">
              <col>
            </colgroup>
            <tbody>
            <tr>
              <th>위치</th>
              <td>
                <select v-model="filterType">
                  <option value="">-- 모든 위치 --</option>
                  <option v-for="opt in bannerTypeOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </td>
            </tr>
            <tr>
              <th>공개 여부</th>
              <td>
                <select v-model="visibilityFilter">
                  <option value="">-- 전체 --</option>
                  <option value="public">공개</option>
                  <option value="private">비공개</option>
                </select>
              </td>
            </tr>
            <tr>
              <th>검색어</th>
              <td>
                <div class="input-search">
                  <input v-model="searchQuery" @keyup.enter="fetchItems" type="text" name="search-input" placeholder="검색어 입력">
                  <i @click="fetchItems" class="button-search" style="cursor: pointer;"><img src="@/assets/images/ico/ico_search.svg" alt=""/></i>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="list-top mt50">
        <h3></h3>
        <div class="control-group">
          <div class="btn-group">
            <button @click="deleteItems" type="button" class="delete-button">삭제</button>
            <button @click="addBanner" type="button" class="navy-button add-button">등록</button>
          </div>
        </div>
      </div>

      <div v-if="isDraggable" class="reorder-hint reorder-hint--on">
        맨 앞 칸의 손잡이 아이콘(점 6개)을 잡고 위아래로 드래그하면 노출 순서가 바뀝니다. ({{ getBannerTypeLabel(filterType) }} · 공개)
      </div>
      <div v-else class="reorder-hint">
        ℹ 순서 변경은 <b>위치</b> + <b>공개 여부 = 공개</b>를 함께 선택하면 활성화됩니다.
      </div>

      <div class="white-panel mt20">
        <div class="table-wrap">
          <div class="scroll-box">
            <table class="table-type1">
              <colgroup>
                <col style="width:60px;">
                <col>
                <col style="width:120px;">
                <col style="width:120px;">
                <col style="width:120px;">
                <col style="width:120px;">
                <col style="width:120px;">
                <col style="width:120px;">
              </colgroup>
              <thead>
              <tr>
                <th>
									<span v-if="!isDraggable" class="checkbox-type1">
										<input @click="toggleAllCheck" type="checkbox" id="allchk">
										<label for="allchk">체크</label>
									</span>
                </th>
                <th>설명</th>
                <th>위치</th>
                <th>파일</th>
                <th>순서</th>
                <th>공개여부</th>
                <th>작성자</th>
                <th>등록일</th>
              </tr>
              </thead>
              <tbody>
              <tr
                v-for="(item, index) in displayItems"
                :key="item.uuid"
                :class="{
                  'bg-hidden': item.isHidden,
                  'drag-row': isDraggable,
                  'drag-over': isDraggable && overIndex === index,
                  'group-start': isGroupStart(item, index),
                }"
                @dragenter.prevent="isDraggable && onDragEnter(index, displayItems, groupOf)"
                @dragover.prevent
                @drop="isDraggable && onDrop(displayItems, groupOf, applyGroupOrder)"
                @dragend="onDragEnd"
              >
                <td>
                    <span
                      v-if="isDraggable"
                      class="drag-handle"
                      :draggable="true"
                      @dragstart="onDragStart(index)"
                      title="여기를 잡고 위아래로 드래그하세요"
                    >
                      <svg width="12" height="18" viewBox="0 0 12 18" fill="currentColor" aria-hidden="true">
                        <circle cx="4" cy="4" r="1.6" /><circle cx="8" cy="4" r="1.6" />
                        <circle cx="4" cy="9" r="1.6" /><circle cx="8" cy="9" r="1.6" />
                        <circle cx="4" cy="14" r="1.6" /><circle cx="8" cy="14" r="1.6" />
                      </svg>
                    </span>
                    <span v-else class="checkbox-type1">
                        <input v-model="selectedItems" :id="item.uuid" type="checkbox" :value="item.uuid">
                        <label :for="item.uuid">체크</label>
                    </span>
                </td>

                <td>
                  <router-link :to="{ name: 'BannerDetail', params: { uuid: item.uuid } }" class="subject">{{ item.description }}</router-link>
                </td>
                <td>{{ getBannerTypeLabel(item.type) }}</td>
                <td>{{ item.mediaType }}</td>
                <td>{{ item.sortOrder }}</td>
                <td>{{ !item.isHidden ? "공개" : "비공개" }}</td>
                <td>{{ item.createdBy }}</td>
                <td>{{ item.createdAt }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {apiClient} from '@/services/auth-header';
import router from '@/router';
import {useDragReorder} from '@/composables/useDragReorder';

const items = ref([]);
const selectedItems = ref([]);
const searchQuery = ref('');
const filterType = ref(''); // '' | HOME_MAIN | HOME_SUB | ART_ARTIST
const visibilityFilter = ref(''); // '' 전체 | 'public' | 'private'
const API_URL = '/contents';

// 배너 위치(type) — 백엔드 BannerType enum 과 1:1
const BANNER_TYPE_LABELS = {
  HOME_MAIN: '홈 메인',
  HOME_SUB: '홈 서브',
  ART_ARTIST: '아트 · 아티스트',
};
const bannerTypeOptions = Object.entries(BANNER_TYPE_LABELS).map(([value, label]) => ({value, label}));
const getBannerTypeLabel = (type) => BANNER_TYPE_LABELS[type] || type;

const {overIndex, onDragStart, onDragEnter, onDragEnd, onDrop} = useDragReorder();

// 드래그 재정렬의 그룹 키 = 위치(type). 순서는 위치 안에서만 의미.
const groupOf = (item) => item.type;

// 위치 + 공개여부 필터 (클라이언트 — 목록은 전량 조회). 공개면 같은 위치끼리 인접 정렬.
const displayItems = computed(() => {
  const rows = items.value.filter((it) => {
    if (filterType.value && it.type !== filterType.value) return false;
    if (visibilityFilter.value === 'public' && it.isHidden) return false;
    if (visibilityFilter.value === 'private' && !it.isHidden) return false;
    return true;
  });
  if (visibilityFilter.value === 'public') {
    return rows.slice().sort((a, b) => a.type.localeCompare(b.type) || a.sortOrder - b.sortOrder);
  }
  return rows;
});

// 드래그 활성화 — 공개 + 특정 위치 선택 시. 단일 그룹만 표시되어 명확.
const isDraggable = computed(
  () => visibilityFilter.value === 'public' && filterType.value !== ''
);

// 위치가 바뀌는 첫 행 표시(그룹 구분선)
const isGroupStart = (item, index) =>
  isDraggable.value && index > 0 && displayItems.value[index - 1].type !== item.type;

// 드롭 시: 영향받은 위치 그룹만 표시 순서대로 sortOrder 0..N 재부여 후 저장
const applyGroupOrder = async (groupRows) => {
  const payload = groupRows.map((item, idx) => ({uuid: item.uuid, sortOrder: idx}));
  try {
    await apiClient.put(API_URL + '/banners/order-bulk', payload);
    await fetchItems();
  } catch (error) {
    console.error('순서 변경 실패', error);
    alert('순서 변경 중 오류가 발생했습니다.');
    await fetchItems();
  }
};

const toggleAllCheck = (event) => {
  if (event.target.checked) {
    selectedItems.value = displayItems.value.map(item => item.uuid);
  } else {
    selectedItems.value = [];
  }
};

const addBanner = () => {
  router.push({
    name: 'BannerCreate'
  });
};

const fetchItems = async () => {
  try {
    const response = await apiClient.get(API_URL + '/banners', {
      params: {q: searchQuery.value}
    });
    items.value = response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

const deleteItems = async () => {
  if (confirm('삭제 하시겠습니까?')) {
    try {
      const response = await apiClient.delete(API_URL + '/banners', {data: selectedItems.value});
      if (response.status === 200) {
        alert('삭제되었습니다.')
        selectedItems.value = [];
        await fetchItems();
      }
    } catch (error) {
      console.error('Error deleting items:', error);
    }
  }
};

onMounted(async () => {
  await fetchItems();
});
</script>
<style scoped>
select {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  max-width: 200px;
}

.reorder-hint {
  margin-top: 24px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  background: #f3f4f6;
  color: #6b7280;
}
.reorder-hint--on {
  background: #eef2ff;
  color: #4338ca;
  font-weight: 600;
}
.reorder-hint b {
  color: #101733;
}
.drag-row:hover {
  background: #f8faff;
}
.drag-over td {
  border-top: 2px solid #4f46e5;
}
.group-start td {
  border-top: 2px solid #d1d5db;
}
.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  padding: 3px 5px;
  border-radius: 4px;
  background: #eef2ff;
  color: #4f46e5;
  cursor: grab;
  user-select: none;
  vertical-align: middle;
}
.drag-handle:active {
  cursor: grabbing;
}
</style>
